import { MUSIC_KEY, sfxForEvent, sfxVolume } from './eventMap.js';
import { audioEvents } from './audioEvents.js';

export const AMBIENCE_VOLUME = 0.25;
export const AMBIENCE_FADE_MS = 1500;
const LS_MUSIC = 'sd_music_muted';
const LS_SFX = 'sd_sfx_muted';
const readFlag = (k) => { try { return localStorage.getItem(k) === '1'; } catch { return false; } };
const writeFlag = (k, v) => { try { localStorage.setItem(k, v ? '1' : '0'); } catch { /* ignore */ } };

export class AudioManager {
  constructor() {
    this.sound = null;                 // Phaser sound manager (set in wire())
    this.music = null;                 // the looping music Phaser.Sound
    this.musicMuted = readFlag(LS_MUSIC);
    this.sfxMuted = readFlag(LS_SFX);
    this._wired = false;
    // Route cross-cutting game events to sfx.
    audioEvents.on((name, payload) => {
      const key = sfxForEvent(name);
      if (key) this.playSfx(key, payload?.gain ?? 1);   // gain: e.g. someone else's farm work, quieter (farmFx)
    });
  }

  // Remember the sound manager and start music once audio is unlocked. Safe to call
  // from BootScene.create(). Browsers block audio until the first user gesture; the
  // sound manager is GLOBAL (shared across scenes) and fires 'unlocked' on the first
  // interaction in ANY scene. We must NOT hang this off BootScene's per-scene input —
  // BootScene shuts down before the user ever clicks, taking its listeners with it
  // (that bug let SFX play from live scenes while music never started).
  wire(scene) {
    this.sound = scene.sound;   // all scenes share one global sound manager
    if (this._wired) return;
    this._wired = true;
    this.sound.once('unlocked', () => this.startMusic());  // first gesture, any scene
    if (!this.sound.locked) this.startMusic();             // already unlocked → start now
    // iPhones (owner 2026-10-18: "on mobile there's no music, ever"). The ring/silent switch mutes web
    // audio unless the page plays as media (iOS 17+), and iOS can leave the context suspended or
    // 'interrupted' after a call or an app switch until a tap resumes it — Phaser only unlocks once.
    try { if (globalThis.navigator?.audioSession) navigator.audioSession.type = 'playback'; } catch { /* older iOS */ }
    for (const ev of ['touchend', 'pointerup', 'keydown']) globalThis.addEventListener?.(ev, () => this.wake(), { capture: true, passive: true });
  }

  // Any tap: a context iOS left suspended is resumed, and music that should play but doesn't, starts.
  wake() {
    const ctx = this.sound?.context;
    if (ctx && ctx.state !== 'running') ctx.resume?.().then(() => this.startMusic(), () => {});
    else if (!this.music?.isPlaying) this.startMusic();
  }

  playSfx(key, gain = 1) {
    if (this.sfxMuted || !this.sound) return;
    if (!this.sound.game.cache.audio.exists(key)) return; // clip missing → fail soft
    // Phaser suspends the context while the tab is hidden / the window unfocused; a sound started then
    // waits on the frozen clock and every one of them plays at once on return (owner 2026-10-22). Drop it.
    const ctx = this.sound.context;
    if (ctx && ctx.state !== 'running') return;
    try { this.sound.play(key, { volume: sfxVolume(key) * gain }); } catch (e) { console.warn('sfx failed:', key, e); }
  }

  // One looping ambience (farm 6): the farm's birds / crickets under the music. Muted with the
  // sound effects; `_ambienceWant` remembers what should play so unmuting on the farm starts it again.
  // A change (dusk / dawn) crossfades over AMBIENCE_FADE_MS (owner 2026-09-30).
  playAmbience(key) {
    this._ambienceWant = key;
    if (this.sfxMuted || !this.sound) return;
    if (this.ambience?.key === key && this.ambience.isPlaying) return;
    if (!this.sound.game.cache.audio.exists(key)) return;          // not loaded yet: the scene retries
    const old = this.ambience?.isPlaying ? this.ambience : null;
    if (!old) this.ambience?.destroy?.();
    const next = this.sound.add(key, { loop: true, volume: old ? 0 : AMBIENCE_VOLUME });
    next.key = key;
    next.play();
    this.ambience = next;
    if (old) this._crossfade(old, next);
  }

  _crossfade(old, next) {
    this._endFade();
    const t0 = Date.now(), from = old.volume;
    this._fade = { old, next, timer: setInterval(() => {
      const p = Math.min(1, (Date.now() - t0) / AMBIENCE_FADE_MS);
      old.volume = from * (1 - p);
      next.volume = AMBIENCE_VOLUME * p;
      if (p >= 1) this._endFade();
    }, 50) };
  }

  _endFade() {                            // finish (or abandon) a crossfade: the old loop goes
    if (!this._fade) return;
    clearInterval(this._fade.timer);
    this._fade.old.stop(); this._fade.old.destroy?.();
    if (this._fade.next === this.ambience) this.ambience.volume = AMBIENCE_VOLUME;
    this._fade = null;
  }

  stopAmbience() {
    this._ambienceWant = null;
    this._endFade();
    this.ambience?.stop(); this.ambience?.destroy?.();
    this.ambience = null;
  }

  startMusic() {
    if (!this.sound || this.musicMuted) return;
    if (!this.sound.game.cache.audio.exists(MUSIC_KEY)) return;
    if (!this.music) {
      this.music = this.sound.add(MUSIC_KEY, { loop: true, volume: 0.3 });
    }
    if (!this.music.isPlaying) this.music.play();
  }

  setMusicMuted(muted) {
    this.musicMuted = muted;
    writeFlag(LS_MUSIC, muted);
    if (muted) { this.music?.stop(); }
    else { this.startMusic(); }
  }

  setSfxMuted(muted) {
    this.sfxMuted = muted;
    writeFlag(LS_SFX, muted);
    if (muted) { this._endFade(); this.ambience?.stop(); this.ambience?.destroy?.(); this.ambience = null; }
    else if (this._ambienceWant) this.playAmbience(this._ambienceWant);
  }
}

export const audioManager = new AudioManager();
